import { createFileRoute, Outlet, redirect } from '@tanstack/react-router';
import { fetchPurchaseOrderById } from '@/api/orders';
import { PurchaseOrderDetailHeader } from '@/app/(client)/orders/-components/PurchaseOrderDetailHeader';
import { SalesOrderDetailHeaderSkeleton } from '@/app/(client)/orders/-components/SalesOrderDetailHeaderSkeleton';
import {
  purchaseOrderDetailKeys,
  usePurchaseOrderDetail,
} from '@/app/(client)/orders/-hooks/usePurchaseOrderDetail';
import type { TabEntry } from '@/components/shared/PageTabs';
import PageTabs from '@/components/shared/PageTabs';
import { useTranslator } from '@/hooks/useTranslator';
import { queryClient } from '@/lib/utils/queryClient';

export const Route = createFileRoute(
  '/(client)/orders/(pages)/(detail)/purchase-orders/$poId',
)({
  beforeLoad: ({ params, location }) => {
    const bare = `/orders/purchase-orders/${params.poId}`;
    if (location.pathname === bare || location.pathname === `${bare}/`) {
      throw redirect({
        to: '/orders/purchase-orders/$poId/overview',
        params: { poId: params.poId },
      });
    }
  },
  loader: ({ params }) =>
    queryClient.ensureQueryData({
      queryKey: purchaseOrderDetailKeys.detail(params.poId),
      queryFn: () => fetchPurchaseOrderById(params.poId).then(res => res.data),
    }),
  component: PurchaseOrderDetailLayout,
});

function PurchaseOrderDetailLayout() {
  const { poId } = Route.useParams();
  const { getTranslation: t } = useTranslator({ page: 'orders' });
  const { data: order } = usePurchaseOrderDetail(poId);

  const base = `/orders/purchase-orders/${poId}`;
  const tabs: TabEntry[] = [
    { key: 'overview', label: t('detail.tabOverview'), to: `${base}/overview` },
    { key: 'tracing', label: t('detail.tabTracing'), to: `${base}/tracing` },
    { key: 'products', label: t('detail.tabProducts'), to: `${base}/products` },
    {
      key: 'documents',
      label: t('detail.tabDocuments'),
      to: `${base}/documents`,
    },
  ];

  return (
    <div className='flex flex-col flex-1 min-h-0 overflow-y-auto'>
      {order ? (
        <PurchaseOrderDetailHeader order={order} />
      ) : (
        <SalesOrderDetailHeaderSkeleton />
      )}
      <div className='sticky top-0 z-20 bg-background'>
        <PageTabs tabs={tabs} mode='path' moreLabel={t('detail.moreTabs')} />
      </div>
      <div className='@container flex-1 p-4 sm:p-6 w-full'>
        <Outlet />
      </div>
    </div>
  );
}
